/**
 * Shared quote edit-host field maps + PATCH-payload builder.
 *
 * Extracted from QuoteDetailPage so the grouped-display editor drawer
 * (QuoteLaneEditorDrawer) reuses the EXACT same camelCase→snake_case mapping,
 * change-diffing, dirty check and save-error wording — no duplicated DIRECT_MAP
 * / patch logic. Both the full detail page (header + revision + direct +
 * warehouse) and the per-lane drawer (direct + revision) call into this module.
 */
import type { QuoteDetailData, DirectFields, WarehouseFields, QuoteUpdatePayload } from './fixtures';

/* ──────────────────────────────────────────────────────────────────────────
 * Edit-host field maps. camelCase display key (QuoteDetailData / DirectFields)
 * → backend snake_case column. ONLY mapped fields are persisted; anything the
 * tabs make visually editable but is NOT listed here is ignored at save time.
 * ────────────────────────────────────────────────────────────────────────── */

/** masterquote columns (header card). */
export const HEADER_MAP: Partial<Record<keyof QuoteDetailData, keyof NonNullable<QuoteUpdatePayload['header']>>> = {
  // billToNumber/billTo reconcile a deferred billto=0 quote to a real bill-to
  // (V1 editrecord_quote.pl) — the backend re-runs the active-check on the new
  // recno. address fields below re-denormalize the picked master's address.
  billToNumber: 'billto', billTo: 'billtoname',
  // customerCode/customer let a duped (unsecured, revision-1) quote be re-pointed
  // at a different customer; the backend rejects the change on a later revision.
  customerCode: 'customercode', customer: 'customername',
  addr1: 'address1', addr2: 'address2', city: 'city', stateCode: 'state', zip: 'zip',
  billFax: 'billtofax',
  contact: 'contactname', phone: 'phone', fax: 'fax', email: 'email', csr: 'csr', salesCsr: 'salescsr', whseCsr: 'whsecsr',
  sfOpp: 'salesforceopportunityid', other: 'other', contractBid: 'contractnumber',
  salesForceName: 'salesforcename', salesForceRecno: 'salesforcerecno',
  fuelCalc: 'fuelcalctype', rateApplies: 'rateapplies', internalComments: 'mastercomments',
  reviewDate: 'reviewdate',
};
/** Header fields sent as numbers (not strings) in the PATCH payload. */
export const HEADER_NUMBER_FIELDS: ReadonlySet<keyof QuoteDetailData> = new Set(['salesForceRecno', 'billToNumber', 'customerCode']);
/** Header date fields converted MM/DD/YYYY → YYYY-MM-DD via toISO() at payload time. */
export const HEADER_DATE_FIELDS: ReadonlySet<keyof QuoteDetailData> = new Set(['reviewDate']);

/** quoterevisions columns. Dates (effective/endDate/statusDate) are converted
 *  MM/DD/YYYY → YYYY-MM-DD via toISO() when the payload is built. The Status
 *  control edits `rawStatus` (raw quoterevisions.status — UNSECURED/SECURED),
 *  NOT the derived display `status`; that's why `rawStatus → status` here. */
export const REVISION_MAP: Partial<Record<keyof QuoteDetailData, keyof NonNullable<QuoteUpdatePayload['revision']>>> = {
  rawStatus: 'status', revReason: 'businesstype', volume: 'volume',
  effective: 'effectivedate', endDate: 'enddate', statusDate: 'securedate',
  estRev: 'estimatedrevenue', estMargin: 'estimatedmargin', soldBy: 'soldby',
};
export const REVISION_DATE_FIELDS: ReadonlySet<keyof QuoteDetailData> = new Set(['effective', 'endDate', 'statusDate']);
export const REVISION_NUMBER_FIELDS: ReadonlySet<keyof QuoteDetailData> = new Set(['volume', 'estRev', 'estMargin']);

/** directquote columns. */
export const DIRECT_MAP: Partial<Record<keyof DirectFields, keyof NonNullable<QuoteUpdatePayload['direct']>>> = {
  moveType: 'movetype', size: 'size', equipType: 'equiptype', commodity: 'commodity', lbs: 'lbs', kgs: 'kgs',
  legalWeight: 'legalweight', overweight: 'overweight', oog: 'oog', triaxle: 'triaxle', swing: 'swing', hazmat: 'hazmat',
  chassisType: 'chassistype', rateZone: 'ratezone', deliveryZone: 'deliveryzone',
  deliverToName: 'delivertoname', deliverToAddr1: 'delivertoaddr1', deliverToAddr2: 'delivertoaddr2',
  deliverToCity: 'delivertocity', deliverToState: 'delivertostate', deliverToZip: 'delivertozip',
  deliverToCode: 'delivertocode',
  deliverToMiles: 'delivertomiles', drayRate: 'drayrate', drayType: 'draytype',
  fscRate: 'fscrate', fscType: 'fsctype', fscTariff: 'fsctariff', comments: 'comments', ess: 'hvc', cargoValue: 'cargovalue',
  lengthInches: 'lengthinches', widthInches: 'widthinches', heightInches: 'heightinches',
  waitingTimeFree: 'waitingtimefree', yardStorageFree: 'yardstoragefree',
  chassisFeeType: 'chassisfeetype', chassisFeeDays: 'chassisfeedays',
  lightweightChassisFeeType: 'lightweightchassisfeetype', lightweightChassisFeeDays: 'lightweightchassisfeedays',
};
/** directquote numeric columns — string draft → Number(v)||0 at payload time. */
export const DIRECT_NUMBER_FIELDS: ReadonlySet<keyof DirectFields> = new Set([
  'deliverToCode',
  'deliverToMiles', 'lbs', 'kgs', 'drayRate', 'fscRate', 'fscTariff', 'cargoValue',
  'lengthInches', 'widthInches', 'heightInches', 'waitingTimeFree', 'yardStorageFree',
  'chassisFeeDays', 'lightweightChassisFeeDays',
]);

/** transloadquote columns (WAREHOUSE quotes). */
export const TRANSLOAD_MAP: Partial<Record<keyof WarehouseFields, keyof NonNullable<QuoteUpdatePayload['transload']>>> = {
  moveType: 'movetype', accountType: 'accounttype', whseType: 'whsetype', rateZone: 'ratezone',
  commodity: 'commodity', packageType: 'packagetype', numUnits: 'numunits',
  packagePerContainer: 'packagepercontainer', hazmat: 'hazmat', size: 'size', equipType: 'equiptype',
  lbs: 'lbs', kgs: 'kgs', legalWeight: 'legalweight', overweight: 'overweight', oog: 'oog',
  triaxle: 'triaxle', swing: 'swing', chassisType: 'chassistype',
  numOverflow: 'numoverflow', perContainers: 'percontainers', warehouse: 'warehouse',
  drayRate: 'drayrate', drayType: 'draytype',
  fuelSurcharge: 'fuelsurcharge', fuelType: 'fueltype', fuelTariff: 'fueltariff',
  drayHandlingCharge: 'drayhandlingcharge', drayHandlingUnitType: 'drayhandlingunittype',
  includeHandlingInDray: 'includehandlingindray',
  storageType: 'storagetype', storageAnniversaryDays: 'storageanniversarydays',
  storageRate: 'storagerate', storageRateLongTerm: 'storageratelongterm',
  storageUnitType: 'storageunittype', storageRcvMonthFree: 'storagercvmonthfree',
  storageFreeDays: 'storagefreedays', storageFreePallets: 'storagefreepallets',
  storageDoNotBillReason: 'storagedonotbillreason',
  comments: 'comments', ess: 'hvc', cargoValue: 'cargovalue',
  lengthInches: 'lengthinches', widthInches: 'widthinches', heightInches: 'heightinches',
  waitingTimeFree: 'waitingtimefree', yardStorageFree: 'yardstoragefree',
  unitsPerContainer: 'unitspercontainer', useSlidingScale: 'useslidingscale',
  chassisFeeType: 'chassisfeetype', chassisFeeDays: 'chassisfeedays',
  lightweightChassisFeeType: 'lightweightchassisfeetype',
  lightweightChassisFeeDays: 'lightweightchassisfeedays',
};
export const TRANSLOAD_NUMBER_FIELDS: ReadonlySet<keyof WarehouseFields> = new Set([
  'packagePerContainer', 'lbs', 'kgs', 'numOverflow', 'perContainers', 'drayRate',
  'fuelSurcharge', 'fuelTariff', 'drayHandlingCharge', 'storageAnniversaryDays',
  'storageRate', 'storageRateLongTerm', 'storageFreeDays', 'storageFreePallets',
  'cargoValue', 'lengthInches', 'widthInches', 'heightInches', 'waitingTimeFree',
  'yardStorageFree', 'unitsPerContainer', 'chassisFeeDays', 'lightweightChassisFeeDays',
]);

/** ""→"", "MM/DD/YYYY"→"YYYY-MM-DD"; an already-ISO value passes through. The
 *  backend translates ""→'0000-00-00'. Anything unrecognised passes through. */
export function toISO(v: string): string {
  const s = (v ?? '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // already ISO
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return s;
  const [, mo, d, y] = m;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** Stringify a draft field value for comparison/normalisation (DirectFields
 *  carries `number | null` on a couple of columns; everything else is string). */
export function asStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

/** Today as MM/DD/YYYY (the display format the date inputs / toISO() expect).
 *  Shared by the edit hosts (QuoteDetailPage + QuoteLaneEditorDrawer) so that
 *  securing a quote stamps a secure date in the format toISO() round-trips. */
export function todayDisplay(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

/** Build the snake_case PATCH payload, including ONLY mapped fields whose value
 *  changed vs the originally loaded quote. Returns the payload plus whether any
 *  change was found (so callers can no-op an empty save). */
export function buildUpdatePayload(
  orig: QuoteDetailData,
  draft: QuoteDetailData,
  origDirect: DirectFields | null | undefined,
  draftDirect: DirectFields | null | undefined,
  origWarehouse?: WarehouseFields | null,
  draftWarehouse?: WarehouseFields | null,
): { payload: QuoteUpdatePayload; changed: boolean } {
  const header: NonNullable<QuoteUpdatePayload['header']> = {};
  const revision: NonNullable<QuoteUpdatePayload['revision']> = {};
  const direct: NonNullable<QuoteUpdatePayload['direct']> = {};
  const transload: NonNullable<QuoteUpdatePayload['transload']> = {};
  let changed = false;

  (Object.keys(HEADER_MAP) as Array<keyof QuoteDetailData>).forEach((k) => {
    const col = HEADER_MAP[k]!;
    if (asStr(orig[k]) !== asStr(draft[k])) {
      changed = true;
      (header as Record<string, unknown>)[col] =
        HEADER_NUMBER_FIELDS.has(k) ? (Number(draft[k]) || 0)
          : HEADER_DATE_FIELDS.has(k) ? toISO(asStr(draft[k]))
          : asStr(draft[k]);
    }
  });

  (Object.keys(REVISION_MAP) as Array<keyof QuoteDetailData>).forEach((k) => {
    const col = REVISION_MAP[k]!;
    if (asStr(orig[k]) !== asStr(draft[k])) {
      changed = true;
      if (REVISION_DATE_FIELDS.has(k)) {
        (revision as Record<string, unknown>)[col] = toISO(asStr(draft[k]));
      } else if (REVISION_NUMBER_FIELDS.has(k)) {
        (revision as Record<string, unknown>)[col] = Number(draft[k]) || 0;
      } else {
        (revision as Record<string, unknown>)[col] = asStr(draft[k]);
      }
    }
  });

  if (origDirect && draftDirect) {
    (Object.keys(DIRECT_MAP) as Array<keyof DirectFields>).forEach((k) => {
      const col = DIRECT_MAP[k]!;
      if (asStr(origDirect[k]) !== asStr(draftDirect[k])) {
        changed = true;
        if (DIRECT_NUMBER_FIELDS.has(k)) {
          (direct as Record<string, unknown>)[col] = Number(draftDirect[k]) || 0;
        } else {
          (direct as Record<string, unknown>)[col] = asStr(draftDirect[k]);
        }
      }
    });
  }

  if (origWarehouse && draftWarehouse) {
    (Object.keys(TRANSLOAD_MAP) as Array<keyof WarehouseFields>).forEach((k) => {
      const col = TRANSLOAD_MAP[k]!;
      if (asStr(origWarehouse[k]) !== asStr(draftWarehouse[k])) {
        changed = true;
        if (TRANSLOAD_NUMBER_FIELDS.has(k)) {
          (transload as Record<string, unknown>)[col] = Number(draftWarehouse[k]) || 0;
        } else {
          (transload as Record<string, unknown>)[col] = asStr(draftWarehouse[k]);
        }
      }
    });
  }

  const payload: QuoteUpdatePayload = { expectedLastChanged: orig.lastChangedToken ?? '' };
  if (Object.keys(header).length) payload.header = header;
  if (Object.keys(revision).length) payload.revision = revision;
  if (Object.keys(direct).length) payload.direct = direct;
  if (Object.keys(transload).length) payload.transload = transload;
  return { payload, changed };
}

/** Dirty check over the mapped subsets only — a non-mapped edit never marks
 *  the quote dirty (and is never persisted). */
export function computeDirty(
  orig: QuoteDetailData,
  draft: QuoteDetailData,
  origDirect: DirectFields | null | undefined,
  draftDirect: DirectFields | null | undefined,
  origWarehouse?: WarehouseFields | null,
  draftWarehouse?: WarehouseFields | null,
): boolean {
  return buildUpdatePayload(orig, draft, origDirect, draftDirect, origWarehouse, draftWarehouse).changed;
}

/** Error-banner message for a failed updateQuote (mirrors the wizard's helper):
 *  surface a string `detail` on a 4xx, otherwise a generic message. */
export function saveErrorMessage(err: unknown): string {
  const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const status = resp?.status ?? 0;
  const detail = resp?.data?.detail;
  if (status >= 400 && status < 500 && typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return 'Failed to save quote. Please try again.';
}
